/**
 * Vehicle Hooks
 *
 * Provides hooks for vehicle CRUD operations with TanStack Query.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import { fromOffsetResponse, usePagedQuery } from './usePagedQuery';

// API types
export interface VehicleListItem {
  id: string;
  customerId: string;
  registrationNumber: string;
  make: string | null;
  model: string | null;
  year: number | null;
  vin: string | null;
  mileage: number | null;
  lastServiceDate: string | null;
  notes?: string | null;
  traficomData?: Record<string, unknown>;
  /** Hand-corrected registry overlay (#2337). Display: override ?? traficomData. */
  manualOverrides?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  customerName: string;
  customerPhone: string;
  customerType?: 'individual' | 'business';
  customerRole?: 'owner' | 'holder' | 'work_orderer';
  linkId?: string;
  /** Party identifiers behind the link (#833 B1) — null for legacy rows. */
  partyType?: 'person' | 'company' | null;
  personId?: string | null;
  companyId?: string | null;
}

export interface VehicleCustomerLink {
  id: string;
  vehicleId: string;
  customerId: string;
  role: 'owner' | 'holder' | 'work_orderer';
  startedAt: string;
  endedAt: string | null;
  customerFirstName: string;
  customerLastName: string;
  customerPhone: string | null;
  customerEmail: string | null;
  customerType: string;
  businessName: string | null;
  /** Party identifiers behind the link (#833 B1-prep). */
  partyType?: 'person' | 'company';
  personId?: string | null;
  companyId?: string | null;
}

export interface VehicleDetail {
  id: string;
  customerId: string;
  registrationNumber: string;
  make: string | null;
  model: string | null;
  year: number | null;
  vin: string | null;
  mileage: number | null;
  lastServiceDate: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  traficomData?: Record<string, unknown>;
  /** Hand-corrected registry overlay (#2337). Display: override ?? traficomData. */
  manualOverrides?: Record<string, unknown> | null;
  customer: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    email: string | null;
  } | null;
  owner: VehicleCustomerLink | null;
  holder: VehicleCustomerLink | null;
  workOrderers: VehicleCustomerLink[];
}

export interface ServiceHistoryEntry {
  workOrderNumber?: string | null;
  id: string;
  scheduledAt: string;
  completedAt: string | null;
  /** NULL kun työmääräimellä ei ole palveluita — ks. booking.schema.ts:55. */
  serviceType: string | null;
  status: string;
  totalCostCents: number | null;
  mechanicName: string | null;
  mileage: number | null;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export interface VehicleFilters {
  [key: string]: string | number | boolean | undefined;
  q?: string;
  customerId?: string;
  /** Party-native filters (#833 B4): vehicles actively linked to a person/company. */
  personId?: string;
  companyId?: string;
  make?: string;
  sortBy?: 'registrationNumber' | 'make' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  /**
   * One row per vehicle rather than one per party link (#2204). A vehicle list
   * wants this; the work-order search does not — it offers the same car once
   * per role so the user can pick a party.
   */
  distinctVehicles?: boolean;
}

interface CreateVehicleData {
  /** Legacy customer id. Optional — send `owner` instead (#833 B1). */
  customerId?: string;
  /** Party-native owner (person/company). Either this or customerId is required. */
  owner?: {
    partyType: 'person' | 'company';
    personId?: string | null;
    companyId?: string | null;
  };
  registrationNumber: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  mileage?: number;
  notes?: string | null;
}

interface UpdateVehicleData {
  registrationNumber?: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  mileage?: number;
  notes?: string | null;
  traficomData?: Record<string, unknown>;
  /** Replace the overlay. `null` clears every hand correction. */
  manualOverrides?: Record<string, unknown> | null;
}

// Stats response from GET /vehicles/stats
export interface VehicleStats {
  totalCount: number;
  addedThisMonth: number;
  servicedThisWeek: number;
  makeDistribution: { make: string; count: number }[];
}

/**
 * Hook to fetch vehicle statistics
 */
export function useVehicleStats() {
  return useQuery({
    queryKey: queryKeys.vehicles.stats(),
    queryFn: () => api.get<VehicleStats>('/vehicles/stats'),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch paginated list of vehicles with infinite scroll
 */
export function useVehicles(
  filters: VehicleFilters = {},
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.vehicles.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<VehicleListItem>>('/vehicles', {
        params: {
          ...filters,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 30 * 1000, // 30 seconds
    enabled: options?.enabled ?? true,
  });
}

/** Filters for the page-number list. The infinite hook's cursor fields stay off this request. */
export interface VehiclePageFilters {
  [key: string]: string | number | boolean | undefined;
  q?: string;
  customerId?: string;
  personId?: string;
  companyId?: string;
  make?: string;
  /**
   * One row per vehicle rather than one per party link (#2204).
   */
  distinctVehicles?: boolean;
}

interface OffsetVehicleListResponse {
  data: VehicleListItem[];
  pagination: { total: number };
}

/**
 * Page-number vehicle list (#2740). Always sends `limit` and `offset`
 * (including 0) so the route stays in offset mode. `useVehicles` is unchanged
 * for callers that still scroll.
 */
export function useVehiclesPage(
  filters: VehiclePageFilters = {},
  options?: {
    enabled?: boolean;
    /** Detail-page sections show fewer rows than the full list (#2743). */
    defaultRowsPerPage?: number;
    rowsPerPageOptions?: readonly number[];
  },
) {
  return usePagedQuery({
    queryKey: [...queryKeys.vehicles.list(filters), 'offset'],
    filters,
    ...(options?.defaultRowsPerPage !== undefined ? { defaultRowsPerPage: options.defaultRowsPerPage } : {}),
    ...(options?.rowsPerPageOptions !== undefined ? { rowsPerPageOptions: options.rowsPerPageOptions } : {}),
    defaultSort: { sortBy: 'registrationNumber', sortDir: 'asc' },
    staleTime: 30 * 1000,
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
    fetchPage: ({ filters: pageFilters, limit, offset, sortBy, sortDir }) => {
      const params: Record<string, string | number | boolean | undefined> = {
        ...pageFilters,
        limit,
        offset,
      };
      if (sortBy !== undefined) params.sortBy = sortBy;
      if (sortDir !== undefined) params.sortDir = sortDir;
      return api
        .get<OffsetVehicleListResponse>('/vehicles', { params })
        .then(fromOffsetResponse);
    },
  });
}

/**
 * Hook to fetch a single vehicle by ID
 */
export function useVehicle(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.vehicles.detail(id ?? ''),
    queryFn: () => api.get<VehicleDetail>(`/vehicles/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000, // 1 minute
  });
}

// Mileage history entry from GET /vehicles/:id/mileage/history
export interface MileageHistoryEntry {
  mileage: number;
  recordedAt: string | null;
  source: string | null;
}

// Latest mileage response from GET /vehicles/:id/mileage
export interface LatestMileageResponse {
  mileage: number | null;
  recordedAt: string | null;
  source: string | null;
}

/**
 * Hook to fetch the latest mileage reading for a vehicle
 */
export function useLatestMileage(vehicleId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.vehicles.mileage(vehicleId ?? ''),
    queryFn: () => api.get<LatestMileageResponse>(`/vehicles/${vehicleId}/mileage`),
    enabled: !!vehicleId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch mileage history for a vehicle
 */
export function useMileageHistory(vehicleId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.vehicles.mileageHistory(vehicleId ?? ''),
    queryFn: () => api.get<{ data: MileageHistoryEntry[] }>(`/vehicles/${vehicleId}/mileage/history`),
    enabled: !!vehicleId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to fetch a vehicle's service history
 */
export function useVehicleHistory(vehicleId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.vehicles.history(vehicleId ?? ''),
    queryFn: () =>
      api.get<{ data: ServiceHistoryEntry[] }>(`/vehicles/${vehicleId}/history`),
    enabled: !!vehicleId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to create a new vehicle
 */
export function useCreateVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVehicleData) =>
      api.post<VehicleDetail>('/vehicles', data),
    onSuccess: () => {
      // Invalidate vehicle lists + stats. Party-scoped vehicle lists
      // (persons/companies) are keyed under queryKeys.vehicles.list(filters),
      // so the lists() invalidation covers them (#833 C3).
      void queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.stats(),
      });
    },
  });
}

/**
 * Hook to update an existing vehicle
 */
export function useUpdateVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVehicleData }) =>
      api.patch<VehicleDetail>(`/vehicles/${id}`, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific vehicle and lists
      void queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.lists(),
      });
      // Registration / make / model are denormalized onto work order views (#2643).
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.details(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.myServices(),
      });
    },
  });
}

/**
 * Hook to delete a vehicle
 */
export function useDeleteVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/vehicles/${id}`),
    onSuccess: (_, id) => {
      // Invalidate specific vehicle, lists, and stats
      void queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.stats(),
      });
    },
  });
}

/** Party-native owner input for the change-owner/holder endpoints (#833 B2). */
export interface VehicleOwnerInput {
  partyType: 'person' | 'company';
  personId?: string | null;
  companyId?: string | null;
}

/**
 * Hook to change vehicle owner (party-native, #833 B2)
 */
export function useChangeVehicleOwner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vehicleId, owner }: { vehicleId: string; owner: VehicleOwnerInput }) =>
      api.patch<{ owner: VehicleCustomerLink }>(`/vehicles/${vehicleId}/owner`, { owner }),
    onSuccess: (_, { vehicleId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.detail(vehicleId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.lists(),
      });
    },
  });
}

/**
 * Hook to change or remove vehicle holder (party-native, #833 B2).
 * Pass owner: null to clear the holder.
 */
export function useChangeVehicleHolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vehicleId, owner }: { vehicleId: string; owner: VehicleOwnerInput | null }) =>
      api.patch<{ holder: VehicleCustomerLink | null }>(
        `/vehicles/${vehicleId}/holder`,
        owner ? { owner } : { customerId: null },
      ),
    onSuccess: (_, { vehicleId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.detail(vehicleId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.vehicles.lists(),
      });
    },
  });
}

/**
 * Hook to fetch all customer links for a vehicle (including history)
 */
export function useVehicleCustomers(vehicleId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.vehicles.detail(vehicleId ?? ''), 'customers'],
    queryFn: () =>
      api.get<{ data: VehicleCustomerLink[] }>(`/vehicles/${vehicleId}/customers`),
    enabled: !!vehicleId,
    staleTime: 60 * 1000,
  });
}

/**
 * Vehicle makes and their settings (#2201).
 *
 * The make list is derived from the fleet, not from a registry: a make appears
 * because a vehicle carries it. Settings are the only writable part, which is
 * why the make travels as a path segment and the write is an upsert.
 *
 * Separate from `useVehicleMakesModels` in `useChecklists.ts`, which serves the
 * two flat autocomplete lists and stays as it is.
 */
export interface VehicleMakeSettings {
  id: string;
  make: string;
  serviceBookUrl: string | null;
  serviceBookName: string | null;
  serviceBookYearFrom: number | null;
  serviceBookYearTo: number | null;
  serviceIntervalKm: number | null;
  serviceIntervalMonths: number | null;
  defaultSupplierId: string | null;
  partNumberFormat: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleMakeModel {
  model: string;
  vehicleCount: number;
  /** Oldest and newest model year seen for this model, null when none recorded. */
  yearFrom: number | null;
  yearTo: number | null;
}

export interface VehicleMake {
  /** The most common spelling in the fleet — vehicle rows are never rewritten. */
  make: string;
  vehicleCount: number;
  models: VehicleMakeModel[];
  settings: VehicleMakeSettings | null;
}

/** Fields a make's settings can be written with: null clears one, an omitted one keeps its value (#3797). */
export type VehicleMakeSettingsInput = Partial<
  Omit<VehicleMakeSettings, 'id' | 'make' | 'createdAt' | 'updatedAt'>
>;

/**
 * The whole make list in one query. It is one row per make — tens of them in a
 * workshop, not thousands — so the page filters in the browser rather than
 * refetching per keystroke.
 */
export function useVehicleMakes() {
  return useQuery({
    queryKey: queryKeys.vehicles.makes(),
    queryFn: () => api.get<{ data: VehicleMake[] }>('/vehicles/makes'),
    select: (response) => response.data,
    staleTime: 60 * 1000,
  });
}

function useInvalidateMakes() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.makes() });
}

export function useSaveVehicleMakeSettings() {
  const invalidate = useInvalidateMakes();
  return useMutation({
    mutationFn: ({ make, settings }: { make: string; settings: VehicleMakeSettingsInput }) =>
      api.put<VehicleMakeSettings>(
        `/vehicles/makes/${encodeURIComponent(make)}/settings`,
        settings,
      ),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useDeleteVehicleMakeSettings() {
  const invalidate = useInvalidateMakes();
  return useMutation({
    mutationFn: (make: string) =>
      api.delete(`/vehicles/makes/${encodeURIComponent(make)}/settings`),
    onSuccess: () => {
      void invalidate();
    },
  });
}
